import { useMutation, useQueryClient } from '@tanstack/react-query';

import { VesselUpdateRequestMessage } from '../../../../types/VesselUpdateRequestMessage';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { deleteLubricationChart, updateVessel as updateVesselApi } from '../../../common/api/clients/vessel.api';
import { queries } from '../../../common/api/queryKeys/queries';
import { translate } from '../../../common/helpers/translate.helper';

import type { LubricationChartUpload } from './useLubricationChartUpload';

export enum FileMode {
    ADDED = 'ADDED',
    REMOVED = 'REMOVED',
}

interface UpdateVesselProps {
    vesselId: string;
    vessel: VesselUpdateRequestMessage;
    lubricationChart?: File;
    fileMode?: FileMode;
}

type AddToast = ReturnType<typeof useToasts>['addToast'];
type AddErrorToast = ReturnType<typeof useToasts>['addErrorToast'];

const updateVessel = async (
    { vesselId, vessel, lubricationChart, fileMode }: UpdateVesselProps,
    uploadLubricationChart: LubricationChartUpload['uploadLubricationChart'],
    addToast: AddToast,
    addErrorToast: AddErrorToast
) => {
    if (lubricationChart && fileMode === FileMode.ADDED) {
        try {
            await updateVesselApi({
                vesselId,
                vessel,
            });
            addToast(translate('vessel.vesselUpdateSuccessToast'));
        } catch (error) {
            addErrorToast(translate('vessel.vesselUpdateErrorToast'));
            throw error;
        }

        return uploadLubricationChart(lubricationChart);
    } else if (fileMode === FileMode.REMOVED) {
        await updateVesselApi({
            vesselId,
            vessel,
        });
        return deleteLubricationChart(vessel.id);
    }

    return updateVesselApi({
        vesselId: vessel.id,
        vessel,
    });
};

export const useVesselUpdate = (
    id: string,
    uploadLubricationChart: LubricationChartUpload['uploadLubricationChart']
) => {
    const queryClient = useQueryClient();
    const { addToast, addErrorToast } = useToasts();

    return useMutation({
        mutationFn: (props: UpdateVesselProps) => updateVessel(props, uploadLubricationChart, addToast, addErrorToast),

        onSuccess: (_response, props) => {
            if (props.fileMode !== FileMode.ADDED) {
                queryClient.invalidateQueries({ queryKey: queries.vessels.detail(id).queryKey });
            }
            queryClient.invalidateQueries({ queryKey: queries.vessels.detail(id)._ctx.schedulesWithPrices.queryKey });

            if (props.fileMode !== FileMode.ADDED) {
                addToast(translate('vessel.vesselUpdateSuccessToast'));
            }
        },

        onError: (_error, props) => {
            if (props.fileMode !== FileMode.ADDED) {
                addErrorToast(translate('vessel.vesselUpdateErrorToast'));
            }
        },
    });
};
